// Import react elements
import React, { useEffect, useMemo } from "react";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";

// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";
import { CustomProductLinkSelector, FindCustomProductLinkByMMM } from "./CustomProductMMMLinkSelector";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { InfoButton } from "global/components/InfoButton";



// Render print area details inside the custom product editor
const RenderPrintAreaDetails = ({
    selectedPrintAreaData,
    updateExistingPrintArea,
    selectedCustomProduct,
    setSelectedCustomProduct,
    setSelectedPrintAreaData,
    printAreaTabs,
    renderPrintAreaTabContent,
    activePrintAreaTab,
    setActivePrintAreaTab,
    selectedSurfaceData,
    setSelectedSurfaceData,
    customProductsMMMLinks,
    printAreaRoles,
    setPrintAreaRoles,
    onDirtyChange,
}) => {

    // Define toast options - successful changes are reflected directly in the UI
    const { toastError } = useToast();

    // Custom hook for fetching and managing product data
    const {
        deletePrintArea,
        fetchPrintAreaRoles
    } = useCustomProductsData();


    // Get print area roles for the role dropdown
    useEffect(() => {

        if (!printAreaRoles) {
            fetchPrintAreaRoles(setPrintAreaRoles);
        }

    // Re-run when printAreaRoles changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printAreaRoles]);


    // Set the print area data
    const printAreaData = selectedPrintAreaData;


    // Use the form handler for this single object
    const {
        formValues,
        handleChange,
        fieldErrors,
        isFormPartlyEdited,
    } = useFormHandler({
        initialValues: printAreaData,
        syncInitialValues: true,
        convertBooleans: true,
        convertNumbers: false,
        validate: (values) => {
        }
    });

    // Work out whether changing tree node would discard local print area changes
    const printAreaHasUnsavedChanges = isFormPartlyEdited();

    // Tell the parent popup whether this nested editor is currently dirty
    useEffect(() => {

        onDirtyChange(printAreaHasUnsavedChanges);

        // Clear the dirty flag when this print area editor unmounts
        return () => onDirtyChange(false);

    }, [printAreaHasUnsavedChanges, onDirtyChange]);


    // Read current ids from formValues
    const materialId = formValues?.material_id || "";
    const methodId = formValues?.method_id || "";
    const modeId = formValues?.mode_id || "";

    // Resolve the selected material / method / mode link
    const selectedLink = useMemo(() => {

        return FindCustomProductLinkByMMM(
            customProductsMMMLinks,
            materialId,
            methodId,
            modeId
        );

    }, [customProductsMMMLinks, materialId, methodId, modeId]);

    // Build the value prop for the selector
    const selectorValue = {
        materialId: String(materialId || ""),
        methodId: String(methodId || ""),
        modeId: String(modeId || ""),
        link: selectedLink
    };


    // Handle material / method / mode selector changes
    const handleMMMSelectorChange = (nextSelection) => {

        handleChange({ target: { name: "material_id", value: nextSelection.materialId } });
        handleChange({ target: { name: "method_id", value: nextSelection.methodId } });
        handleChange({ target: { name: "mode_id", value: nextSelection.modeId } });

        // Copy linked settings into the print area when the selected link supplies them
        if (nextSelection.link && nextSelection.link.settings) {

            handleChange({
                target: {
                    name: "settings",
                    value: { ...nextSelection.link.settings }
                }
            });

        }

    };


    // Handle local form submit
    const handleSubmitSavePrintArea = async (event) => {

        event.preventDefault();

        const response = await updateExistingPrintArea(
            selectedCustomProduct.code,
            selectedSurfaceData,
            selectedPrintAreaData.code,
            formValues,
            setSelectedCustomProduct,
            setSelectedSurfaceData,
            setSelectedPrintAreaData
        );

        if (response.success) {

            // Make the successfully saved print area the new editor baseline
            setSelectedPrintAreaData(formValues);
            onDirtyChange(false);

        } else {

            // Only failures need a toast
            toastError({ title: "Error", message: "Could not update print area: " + response.error_message });

        }

    };


    // Function to handle clicking delete print area
    const handleDeletePrintArea = async () => {

        DisplayConfirmationDialog(
            "Are you sure you want to delete this print area: " + formValues.name + "?",
            "Delete",
            "Cancel",
            async function (is_confirmed) {

                if (is_confirmed) {

                    const response = await deletePrintArea(
                        selectedCustomProduct.code,
                        selectedSurfaceData,
                        selectedPrintAreaData.code,
                        selectedCustomProduct.id,
                        setSelectedCustomProduct,
                        setSelectedSurfaceData
                    );

                    if (response.success) {

                        // Return to the parent Surface after deletion
                        onDirtyChange(false);
                        setSelectedPrintAreaData(null);

                    } else {

                        toastError({ title: "Error", message: "Could not delete print area: " + response.error_message });

                    }

                }

            }
        );

    };


    // Get the selected role so its description can be shown
    const selectedRole = formValues.role
        ? printAreaRoles?.find((printAreaRole) => printAreaRole.role === formValues.role)
        : null;


    return (

        <div className="custom-product-editor__panel">

            <div className="custom-product-editor__section-heading">
                <h3>Print Area: {formValues.name || printAreaData.code}</h3>
            </div>

            <form id="printAreaDetailsForm" onSubmit={handleSubmitSavePrintArea}>

                <div className="print-area-details__form-layout">

                    {/* Core print area fields stay together on the left */}
                    <div className="print-area-details__form-main">

                        <TwoColumnList
                            property={<label>Name</label>}
                            value={<input name="name" type="text" placeholder="Name" value={formValues.name || ""} onChange={handleChange} required />}
                        />

                        <TwoColumnList
                            property={<label>Code</label>}
                            value={<input name="code" type="text" placeholder="Code" value={formValues.code || ""} onChange={handleChange} required />}
                        />

                        <TwoColumnList
                            property={
                                <label className="horizontal-align-content-center">
                                    Role
                                    <InfoButton
                                        shortLabel="Role Description"
                                        description={selectedRole?.description ? selectedRole.name + ": " + selectedRole.description : "Select a role to view its description..."}
                                    />
                                </label>
                            }
                            value={
                                <select name="role" value={formValues.role || ""} onChange={handleChange}>
                                    <option value="">Select...</option>
                                    {printAreaRoles?.map((role) =>
                                        <option key={role.role} value={role.role} title={role.description}>{role.name} ({role.role})</option>
                                    )}
                                </select>
                            }
                        />

                        <TwoColumnList
                            property={<label>Description</label>}
                            value={<textarea name="description" rows="1" cols="30" value={formValues.description || ""} onChange={handleChange} placeholder="Description"></textarea>}
                        />

                        {fieldErrors.description &&
                            <p style={{ color: "red" }}>{fieldErrors.description}</p>
                        }

                        <TwoColumnList
                            property={<label>Width (mm)</label>}
                            value={<input name="width" type="number" min="0" placeholder="0.00" step="0.01" value={formValues.width || ""} onChange={handleChange} required disabled />}
                        />

                        <TwoColumnList
                            property={<label>Height (mm)</label>}
                            value={<input name="height" type="number" min="0" step="0.01" placeholder="0.00" value={formValues.height || ""} onChange={handleChange} required disabled />}
                        />

                        <TwoColumnList
                            property={<label>Print Price</label>}
                            value={<input name="nominal_print_price" type="number" min="0" step="any" placeholder="Print Price" value={formValues.nominal_print_price || ""} onChange={handleChange} required />}
                        />

                        <TwoColumnList
                            property={
                                <>
                                    <label>Print Profit</label>

                                    <InfoButton
                                        shortLabel="Print Profit"
                                        description="A minimim profit amount for this print area. This is used to calculate the RRP based on the configuration of the product and it's print areas."
                                    />
                                </>
                            }
                            value={
                                <input
                                    name="nominal_print_profit"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="Print Profit"
                                    value={formValues.nominal_print_profit === null || formValues.nominal_print_profit === undefined ? "" : formValues.nominal_print_profit}
                                    onChange={(event) => {
                                        handleChange({
                                            target: {
                                                name: "nominal_print_profit",
                                                value: event.target.value === "" ? null : event.target.value
                                            }
                                        });
                                    }}
                                />
                            }
                        />

                    </div>

                    {/* Production method and its derived settings sit together on the right */}
                    <div className="print-area-details__form-production">

                        <div className="edit-custom-product-link-form__field">
                            <CustomProductLinkSelector
                                links={customProductsMMMLinks}
                                value={selectorValue}
                                onChange={handleMMMSelectorChange}
                            />
                        </div>

                        <div className="print-area-details__settings-container">

                            <p className="two-column-list__key">Settings:</p>

                            {printAreaData.settings &&

                                <div className="print-area-details__settings">

                                    {Object.entries(printAreaData.settings).map(([key, value]) => (

                                        <div className="settings-list__item" key={key}>
                                            <span className="settings-list__key">{key}</span>
                                            <span className="settings-list__value">{value || ""} </span>
                                        </div>

                                    ))}

                                </div>

                            }

                        </div>

                    </div>

                </div>

            </form>


            {/* Views and Positions are genuine sibling collections, so the tabs remain here */}
            <div className="tabs-header">

                {printAreaTabs.map((tab, index) => (

                    <div
                        key={index}
                        className={`tabs-header__tab ${activePrintAreaTab === index ? "tabs-header__tab--active" : ""}`}
                        onClick={() => setActivePrintAreaTab(index)}
                    >
                        {tab.label}
                    </div>

                ))}

                <div
                    className="tabs-header__indicator"
                    style={{ transform: `translateX(${activePrintAreaTab * 100}%)`, width: `calc(100% / ${printAreaTabs.length})` }}
                />

            </div>

            <div className="tabs-scroll-container">
                <div className="tabs-content">
                    {renderPrintAreaTabContent()}
                </div>
            </div>


            {/* Print area actions stay with this editor; navigation is handled by the tree */}
            <div className="custom-product-editor__section-actions horizontal-align-content-center flex-justify-left-right">

                <CreateButton
                    title="Delete Print Area"
                    isDelete={true}
                    tooltip="Delete Print Area"
                    size="m"
                    icon="/images/icon_bin.png"
                    onClick={() => handleDeletePrintArea()}
                />

                {printAreaHasUnsavedChanges &&

                    <CreateButton
                        type="submit"
                        title="Save changes"
                        size="m"
                        formID="printAreaDetailsForm"
                    />

                }

            </div>

        </div>

    );

}; export default RenderPrintAreaDetails;
